/**
 * Drives the generic OAuth-popup connect flow for a unified connection: starts
 * the connection (server returns an authorize URL), opens a popup, waits for the
 * public callback relay to hand back `code + state`, then completes it. Shared
 * by every OAuth-shaped connect-kind (oauth2, slack, microsoft, teams) so the
 * Linked Accounts pane and the channel-link landing page have one connect path,
 * no per-provider code.
 *
 * WHY TWO RESULT CHANNELS: a provider that sends `Cross-Origin-Opener-Policy`
 * (Slack does; more do every year) moves the popup into a fresh browsing-context
 * group, permanently severing `window.opener`. Two things then break at once —
 * the relay's `postMessage` has no opener to reach, and `popup.closed` starts
 * reporting `true` for a window that is still open in front of the user. So the
 * relay also writes the result to same-origin `localStorage`, and this hook
 * takes whichever channel arrives first. `closed` is treated as advisory only:
 * acting on it is what produced a "Popup closed before completion." error while
 * the user was still looking at the provider's consent screen.
 */

import { useCallback } from "react";
import { toast } from "sonner";

import {
  meConnectionsService,
  type ConnectionProfileInput,
  type MeConnection,
} from "@/services/me-connections-service";

/** Shape the relay hands back over either channel. */
interface OAuthRelayResult {
  source?: string;
  ok?: boolean;
  code?: string | null;
  state?: string | null;
  message?: string | null;
  /** Set by the storage channel only. */
  ts?: number;
}

/** localStorage slot the relay writes for a given `state`. */
function resultKey(state: string | undefined): string {
  return `taqat_oauth_result:${state ?? "nostate"}`;
}

/** Fallback slot, for provider errors that come back without echoing state. */
const LATEST_RESULT_KEY = "taqat_oauth_result:latest";

/**
 * How long to wait for a result before giving up. Generous: it has to cover
 * signing in to the provider, MFA, and picking an account. Only a hung flow
 * ever reaches it, and reaching it is silent — by then the user has moved on.
 */
const RESULT_TIMEOUT_MS = 5 * 60 * 1000;

/** Sentinel for the timeout path, so the caller can stay quiet about it. */
const ABANDONED = "taqat:oauth-abandoned";

/**
 * How long the popup must LOOK closed before we hand the user their button
 * back. `popup.closed` reads `true` within a second or two whenever the
 * provider severs the opener, so reacting immediately would yank the "Waiting
 * for…" state away mid-consent; but a popup the user really did close should
 * not leave a spinner running until the full timeout. Waiting a minute
 * separates the two without ever cancelling the flow itself — a result that
 * lands afterwards is still honoured.
 */
const DETACH_GRACE_MS = 60 * 1000;

function readStoredResult(key: string): OAuthRelayResult | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as OAuthRelayResult;
  } catch {
    return null;
  }
}

function clearStoredResult(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable — nothing to clean up.
  }
}

/**
 * Waits for the relay's result on either channel.
 *
 * @param expectedState - CSRF state this attempt was started with
 * @param startedAt - Timestamp of the attempt, so a stale slot from an earlier
 *                    attempt in this tab can never be mistaken for this one
 * @param popup - The opened window, used only as an advisory idle signal
 * @param onIdle - Called once the popup has looked closed for {@link DETACH_GRACE_MS};
 *                 the wait continues, this just releases the busy state
 */
function waitForOAuthResult(
  expectedState: string | undefined,
  startedAt: number,
  popup: Window,
  onIdle: () => void,
): Promise<{ code: string; state: string }> {
  const key = resultKey(expectedState);
  // A slot left behind by an abandoned earlier attempt would resolve this one
  // instantly with a stale code.
  clearStoredResult(key);

  return new Promise((resolve, reject) => {
    let settled = false;
    let closedSince: number | null = null;
    let idleNotified = false;

    const cleanup = () => {
      settled = true;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      clearInterval(poll);
      clearTimeout(timer);
      clearStoredResult(key);
      clearStoredResult(LATEST_RESULT_KEY);
    };

    const accept = (data: OAuthRelayResult) => {
      if (settled) return;
      cleanup();
      if (!data.ok) return reject(new Error(data.message || "Connection failed."));
      if (!data.code || (expectedState && data.state && data.state !== expectedState)) {
        return reject(new Error("Verification failed — state mismatch."));
      }
      resolve({ code: data.code, state: data.state ?? "" });
    };

    function onMessage(event: MessageEvent) {
      const data = event.data as OAuthRelayResult | null;
      if (!data || data.source !== "taqat-external-oauth") return;
      accept(data);
    }

    function onStorage(event: StorageEvent) {
      if (event.key !== key && event.key !== LATEST_RESULT_KEY) return;
      checkStorage();
    }

    /**
     * Polls as well as listening: `storage` events only fire in OTHER documents
     * of the origin, and a popup that closes itself immediately can land its
     * write in a window where the event is missed.
     */
    function checkStorage() {
      const own = readStoredResult(key);
      if (own && (own.ts ?? 0) >= startedAt) {
        accept(own);
        return;
      }
      // Provider errors sometimes drop `state`; take the latest slot only when
      // it belongs to this attempt (newer, and not some other state's result).
      const latest = readStoredResult(LATEST_RESULT_KEY);
      if (
        latest &&
        (latest.ts ?? 0) >= startedAt &&
        (!latest.state || !expectedState || latest.state === expectedState)
      ) {
        accept(latest);
      }
    }

    /** Advisory only — never settles the promise (see the module comment). */
    function checkIdle() {
      let looksClosed = false;
      try {
        looksClosed = popup.closed;
      } catch {
        // Reference severed — indistinguishable from closed, same handling.
        looksClosed = true;
      }
      if (!looksClosed) {
        closedSince = null;
        return;
      }
      if (closedSince === null) closedSince = Date.now();
      if (!idleNotified && Date.now() - closedSince >= DETACH_GRACE_MS) {
        idleNotified = true;
        onIdle();
      }
    }

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    const poll = setInterval(() => {
      checkStorage();
      checkIdle();
    }, 500);
    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error(ABANDONED));
    }, RESULT_TIMEOUT_MS);
  });
}

/**
 * Returns a `connect(conn)` callback that runs the OAuth-popup dance and calls
 * `onDone` (e.g. to refresh the list) on success. `setBusy` tracks the provider
 * being connected for per-row spinners.
 */
export function useOAuthPopup(params: {
  onDone: () => void | Promise<void>;
  setBusy: (provider: string | null) => void;
}): (conn: MeConnection, profile?: ConnectionProfileInput) => Promise<void> {
  const { onDone, setBusy } = params;
  return useCallback(
    async (conn: MeConnection, profile?: ConnectionProfileInput) => {
      setBusy(conn.provider);
      try {
        const startedAt = Date.now();
        const { authUrl, state, codeVerifier, data } = await meConnectionsService.start(conn.provider);
        if (!authUrl) throw new Error("Could not start the connection.");
        const popup = window.open(authUrl, `connect-${conn.provider}`, "width=560,height=720");
        if (!popup) throw new Error("Popup blocked. Allow popups and try again.");
        const { code, state: returnedState } = await waitForOAuthResult(
          state,
          startedAt,
          popup,
          // Give the button back if the popup looks long gone; the wait itself
          // carries on, so finishing in a still-open window still works.
          () => setBusy(null),
        );
        // Some connect kinds (e.g. teams_oidc) return an OIDC `nonce` from
        // /start in `data` and require it back at /complete to bind the
        // id_token. Forward it when present; harmless for kinds that don't use it.
        const nonce = (data as { nonce?: string } | undefined)?.nonce;
        const result = await meConnectionsService.complete(conn.provider, {
          code,
          state: returnedState || state,
          codeVerifier,
          ...(nonce ? { nonce } : {}),
          profile,
        });
        if (!result.success) throw new Error("Connection could not be completed.");
        toast.success(`${conn.displayName} connected`);
        await onDone();
      } catch (err) {
        // The timeout means the user walked away (or closed the popup) — they
        // already know nothing happened, so don't shout about it.
        if (err instanceof Error && err.message === ABANDONED) return;
        toast.error(err instanceof Error ? err.message : "Connection failed.");
      } finally {
        setBusy(null);
      }
    },
    [onDone, setBusy],
  );
}
